/**
 * Activity Log detail screen.
 *
 * Web counterpart of the native ActivityLogResultModal: resolves an activity log
 * by id (the destination the schedule cards link to) and renders either the
 * workout-result content or the generic activity summary, with a delete action.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { useActivityLog, useDeleteActivityLog } from '../../../Hooks/useActivityQueries';
import { ActivityLog } from '../../../Model/Activity/ActivityLog';
import { LoadingContainer } from '../../../Components/LoadingContainer';
import { Button } from '../../../Components/Button';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { Theme } from '../../../Theme/Theme';
import { ActivityLogWorkoutContent } from './ActivityLogWorkoutContent';
import { ActivityLogGenericContent } from './ActivityLogGenericContent';

/**
 * When an activity log was created by consuming an explore entry
 * (`source.sourceType === 'explore-entry'`), the logged content itself remains
 * watchable. Resolve the in-app content route for it so the result screen can
 * offer a "watch" action — the native ActivityLogResultModal only shows stats,
 * which is unhelpful for webinars/videos the user actually wants to (re)play.
 */
export function activityLogContentPath(log: ActivityLog): string | undefined {
  if (log.source?.sourceType !== 'explore-entry' || !log.source.sourceId) return undefined;
  const id = log.source.sourceId;
  // `log.type` is an activity-category value (e.g. webinar, mindAndBody, yoga,
  // recipe, cardio, …), not an explore objectType. The watch target is
  // `/<section>/<sourceId>`, and the content view resolves and renders the
  // entry by its own type via findOne, so the section only affects URL/nav.
  switch (log.type) {
    case 'recipe':
      return `/nutrition/${id}`;
    case 'mindAndBody':
    case 'meditation':
    case 'breathing':
      return `/mental-health/${id}`;
    case 'article':
    case 'webinar':
      return `/academy/${id}`;
    default:
      return `/move/${id}`;
  }
}

const PageContainer = styled(Container)`
  padding-top: 24px;
  padding-bottom: 48px;
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const IconButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid ${Theme.Colors.lighterGrey};
  background: ${Theme.Colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s ease;

  & > i {
    font-size: 20px;
    color: ${Theme.Colors.textColor};
  }

  &:hover {
    background: ${Theme.Colors.lightestGrey};
  }
`;

const DeleteButton = styled(IconButton)`
  & > i {
    color: ${Theme.Colors.error};
  }
`;

const HeaderTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NotFound = styled.div`
  padding: 64px 24px;
  text-align: center;
  color: ${Theme.Colors.charcoal};
`;

const WatchBar = styled.div`
  margin-bottom: 8px;

  & > button > i {
    margin-right: 6px;
  }
`;

export const ActivityLogDetailScreen: React.FC = observer(() => {
  const { t } = useTrans();
  const navigate = useNavigate();
  const { activityLogId } = useParams<{ activityLogId: string }>();

  const { data: activityLog, isLoading } = useActivityLog(activityLogId);
  const deleteActivityLog = useDeleteActivityLog();

  const watchPath = activityLog ? activityLogContentPath(activityLog) : undefined;
  const handleWatch = React.useCallback(() => {
    if (watchPath) navigate(watchPath);
  }, [watchPath, navigate]);

  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const handleBack = React.useCallback(() => navigate(-1), [navigate]);

  const handleConfirmDelete = React.useCallback(() => {
    if (!activityLogId) return;
    deleteActivityLog.mutate(activityLogId, {
      onSettled: () => {
        setConfirmOpen(false);
        navigate('/activities', { replace: true });
      },
    });
  }, [activityLogId, deleteActivityLog, navigate]);

  if (isLoading) {
    return <LoadingContainer text={t('activity.loading', { defaultValue: 'Loading activity...' })} />;
  }

  if (!activityLog) {
    return (
      <PageContainer>
        <HeaderBar>
          <HeaderLeft>
            <IconButton onClick={handleBack} aria-label={t('globals.back', { defaultValue: 'Back' })}>
              <i className="mdi mdi-arrow-left" />
            </IconButton>
          </HeaderLeft>
        </HeaderBar>
        <NotFound>{t('activity.notFound', { defaultValue: 'This activity could not be found.' })}</NotFound>
      </PageContainer>
    );
  }

  const headerTitle = dayjs(activityLog.startDate).format('ll');

  return (
    <PageContainer>
      <HeaderBar>
        <HeaderLeft>
          <IconButton onClick={handleBack} aria-label={t('globals.back', { defaultValue: 'Back' })}>
            <i className="mdi mdi-arrow-left" />
          </IconButton>
          <HeaderTitle>{headerTitle}</HeaderTitle>
        </HeaderLeft>
        <DeleteButton
          onClick={() => setConfirmOpen(true)}
          aria-label={t('globals.delete', { defaultValue: 'Delete' })}
          data-testid="activityLogDeleteButton"
        >
          <i className="mdi mdi-trash-can-outline" />
        </DeleteButton>
      </HeaderBar>

      {watchPath && (
        <WatchBar>
          <Button color="primary" onClick={handleWatch} data-testid="activityLogWatchButton">
            <i className="mdi mdi-play-circle-outline" /> {t('activity.watchContent', { defaultValue: 'Watch content' })}
          </Button>
        </WatchBar>
      )}

      {activityLog.workoutLog ? (
        <ActivityLogWorkoutContent activityLog={activityLog} />
      ) : (
        <ActivityLogGenericContent activityLog={activityLog} />
      )}

      <Modal isOpen={confirmOpen} toggle={() => setConfirmOpen(false)} centered>
        <ModalHeader toggle={() => setConfirmOpen(false)}>
          {t('activityLogModal.deleteAlert.title', { defaultValue: 'Delete activity?' })}
        </ModalHeader>
        <ModalBody>
          {t('activityLogModal.deleteAlert.message', {
            defaultValue: 'This will permanently remove this logged activity.',
          })}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" onClick={() => setConfirmOpen(false)}>
            {t('globals.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            color="danger"
            size="sm"
            onClick={handleConfirmDelete}
            disabled={deleteActivityLog.isPending}
          >
            {t('globals.delete', { defaultValue: 'Delete' })}
          </Button>
        </ModalFooter>
      </Modal>
    </PageContainer>
  );
});
